import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { AddIcon } from '@treams/treams-icons';
import { Button, Callout, Ellipsis, Heading, Paragraph, Popover, Tabs } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

import { OrganizationGoalsList } from '../OrganizationGoals';
import { StatefulCollectiveGoalsV2 } from 'goals/collective-goals/CollectiveGoalsV2';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

const Container = styled(Box)`
	background: ${(props) => props.theme.colors.whiteBlue};
	overflow-y: auto;
`;

const Footer = styled(Flex)`
	background: ${(props) => props.theme.colors.white};
	border-top: 1px solid ${(props) => props.theme.colors.lightBlue};
`;

function GoalSelectorModal(props: {
	onDismiss: () => void;
	onSelect: (goal: graphql.Goal) => void;
}) {
	const { t } = useTranslation();

	const [goal, setGoal] = useState<graphql.Goal | undefined>(undefined);
	const [tab, setTab] = useState(0);

	return (
		<Flex flexDirection="column" flexGrow={1} width={tab === 0 ? '100vw' : 'auto'}>
			<Box flexGrow={0} flexShrink={0}>
				<Box p={3} pb={0}>
					<Heading>{t('goals.selector.header.title')}</Heading>
				</Box>

				<Callout
					description={t('goals.selector.connectWarning.description')}
					color="yellow"
					mt={3}
					mx={3}
				/>

				<Tabs>
					<Tabs.Item
						selected={tab === 0}
						title={t('goals.tab.collective.title')}
						onClick={() => setTab(0)}
					/>
					<Tabs.Item
						selected={tab === 1}
						title={t('goals.tab.organization.title')}
						onClick={() => setTab(1)}
					/>
				</Tabs>
			</Box>
			<Container flexGrow={1} flexShrink={1}>
				{tab === 0 && (
					<StatefulCollectiveGoalsV2 hideArchived selectedGoal={goal} onSelect={setGoal} />
				)}
				{tab === 1 && (
					<Box p={3}>
						<OrganizationGoalsList hideArchived selectedGoal={goal} onSelect={setGoal} />
					</Box>
				)}
			</Container>
			<Footer p={3} width="100%" flexGrow={0} flexShrink={0}>
				<Box flexGrow={1} />
				<Button
					type="secondary"
					title={t('goals.new.template.footer.cancel')}
					flexGrow={0}
					flexShrink={0}
					onClick={() => props.onDismiss()}
				/>
				<Button
					type="primary"
					title={t('goals.new.template.footer.continue')}
					flexGrow={0}
					flexShrink={0}
					ml={2}
					onClick={() => props.onSelect(goal!)}
					disabled={!goal}
				/>
			</Footer>
		</Flex>
	);
}

const PlainTag = styled(Flex)<{ interactive?: boolean }>`
	border-radius: 5px;
	height: 34px;
	font-size: 14px;
	border: 1px solid ${(props) => props.theme.colors.lightBlueLight};
	align-items: center;
	padding: 0 12px;

	${(props) =>
		props.interactive &&
		`
		cursor: pointer;
		:hover {
			background-color: ${props.theme.colors.whiteBlue};
		}
	`}
`;

export default function GoalSelectorV2(props: {
	personalGoals?: boolean;
	goal?: graphql.Goal;
	onChange: (goal?: graphql.Goal) => void;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const [visible, setVisible] = useState(false);

	if (props.goal) {
		return (
			<Flex>
				<PlainTag mr={2}>
					<Paragraph flexShrink={0} fontWeight="bold">
						{t('goals.selector.selectedGoal')}:&nbsp;
					</Paragraph>
					<Paragraph color="antracite" fontWeight="bold">
						<Ellipsis maxWidth={150}>{localize(props.goal.title).text}</Ellipsis>
					</Paragraph>
				</PlainTag>
				<Button
					small
					type="destructive"
					title={t('goals.selector.deselect')}
					onClick={() => props.onChange(undefined)}
				/>
			</Flex>
		);
	}

	return (
		<Popover.Target
			contents={
				<GoalSelectorModal
					onDismiss={() => setVisible(false)}
					onSelect={(goal) => {
						props.onChange(goal);
						setVisible(false);
					}}
				/>
			}
			visible={visible}
			center
		>
			<PlainTag onClick={() => setVisible(true)} interactive>
				<Box mr={1} flexShrink={0} flexGrow={0} size={16}>
					<AddIcon />
				</Box>
				<strong>{t('goals.selector.select')}</strong>
			</PlainTag>
		</Popover.Target>
	);
}
